import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { getAuthTokens } from '@vacationtracker/shared/services/auth'

const EXPORT_TIME_WAIT_MINUTES = 16
const EXPORT_TIME_POLL_INTERVAL_SECONDS = 10

export interface IUseExportDownloadParams {
  storageKey: string
  onError: (description?: string) => void
}

export interface IUseExportDownloadResult {
  exportInProgress: boolean
  exportLink: string | undefined
  requestExport: (query: Record<string, unknown>, format: 'excel' | 'csv') => Promise<void>
}

/**
 * The export transport shared shape: POST /core/export-new returns the PREDICTED file URL
 * before the file exists, so delivery is polling that URL until it stops 403ing. The pending
 * URL is persisted so a reload resumes the poll instead of losing the export.
 */
export function useExportDownload(params: IUseExportDownloadParams): IUseExportDownloadResult {
  const { storageKey, onError } = params
  const [exportInProgress, setExportInProgress] = useState(false)
  const [exportLink, setExportLink] = useState<string | undefined>(undefined)
  const cancelledRef = useRef(false)
  const pollingRef = useRef(false)

  useEffect(() => {
    cancelledRef.current = false
    pollUrl()
    return () => {
      cancelledRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const requestExport = async (query: Record<string, unknown>, format: 'excel' | 'csv') => {
    try {
      setExportInProgress(true)
      const tokens = await getAuthTokens()
      const result = await axios.post(`${process.env.REACT_APP_API_URL}/core/export-new`, query, {
        headers: {
          'Content-Type': 'application/json',
          Accept: format === 'excel' ? 'application/vnd.ms-excel' : 'text/csv',
          Authorization: `Bearer ${tokens.idToken}`,
        },
      })
      localStorage.setItem(storageKey, `${(Date.now() + EXPORT_TIME_WAIT_MINUTES * 60 * 1000).toString()}|${result.data}`)
      setTimeout(() => {
        pollUrl()
      }, 2000)
    } catch (error) {
      setExportInProgress(false)
      const description = axios.isAxiosError(error) ? error.response?.data?.error || error.message : error instanceof Error ? error.message : String(error)
      onError(description)
    }
  }

  const pollUrl = async (): Promise<void> => {
    if (pollingRef.current) {
      return
    }
    const stored = localStorage.getItem(storageKey)
    const separatorIndex = stored?.indexOf('|') ?? -1
    const expiresAt = stored && separatorIndex > 0 ? Number(stored.slice(0, separatorIndex)) : NaN
    const url = stored && separatorIndex > 0 ? stored.slice(separatorIndex + 1) : undefined
    if (!url || !Number.isFinite(expiresAt) || expiresAt < Date.now()) {
      localStorage.removeItem(storageKey)
      return
    }
    pollingRef.current = true
    setExportInProgress(true)
    try {
      while (!cancelledRef.current && Date.now() < expiresAt) {
        try {
          const response = await fetch(`https://${url}`)
          if (response.ok) {
            setExportLink(`https://${url}`)
            setExportInProgress(false)
            localStorage.removeItem(storageKey)
            return
          }
        } catch {
          // file not ready yet - keep polling
        }
        await new Promise((resolve) => setTimeout(resolve, EXPORT_TIME_POLL_INTERVAL_SECONDS * 1000))
      }
      if (!cancelledRef.current) {
        setExportInProgress(false)
        localStorage.removeItem(storageKey)
        onError()
      }
    } finally {
      pollingRef.current = false
    }
  }

  return { exportInProgress, exportLink, requestExport }
}
